/**
 * Readers of paio-commerce's public menu shared by the app's catalogue
 * (ORDR-60, ORDR-165).
 *
 *   GET /api/v1/commerce/sites/<site>/locations/<location>/menu/   (COMM-135)
 *
 * NAMES. The category's `display_names` for the diner's language, then `en`,
 * then any value. An item's `display_names` (COMM-131) follows the same rule.
 *
 * WHY IT CANNOT BE ORDERED, KEPT APART (FR-RST-010, 011). `availability.status`
 * is read, not the legacy `available`. "Sold out" is TRUE only for
 * EIGHTY_SIXED, so only an 86 is drawn unavailable. NOT_CARRIED (the location
 * does not sell it), NOT_IN_MODE and JURISDICTION (no producer yet), an unknown
 * status and an unreadable `availability` are OMITTED, and counted by
 * `omittedFromPublicMenu`. An omitted JURISDICTION item therefore never carries
 * a restore time. A body with no `availability` at all is an older commerce and
 * keeps the `available === true` rule.
 *
 * ORDR-166 removed `sectionsFromPublicMenu`, the menu in the framework's
 * `MenuSection` shape: it fed only the framework `CustomerOrdering`, which is no
 * longer mounted for a diner. The app's own menu is `../order/catalogue.ts`.
 */

/** The section id and label for items with no category. */
export const UNCATEGORISED_SECTION_ID = "uncategorised";
export const UNCATEGORISED_LABEL = "Other";

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function nonBlank(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

/**
 * A name from a `{lang: name}` map: the diner's language (exact tag, then its
 * primary subtag), then `en`, then any value. Keys are sorted for the last step
 * because jsonb does not keep the order an author wrote them in.
 */
export function localisedName(names: unknown, language: string): string | null {
  const map = record(names);
  if (map === null) {
    return null;
  }
  const tag = language.trim().toLowerCase();
  const primary = tag.split("-")[0] ?? "";
  for (const key of [tag, primary, "en"]) {
    const name = key === "" ? null : nonBlank(map[key]);
    if (name !== null) {
      return name;
    }
  }
  for (const key of Object.keys(map).sort()) {
    const name = nonBlank(map[key]);
    if (name !== null) {
      return name;
    }
  }
  return null;
}

/** The clock and zone an 86's return time is said against. */
export interface MenuClock {
  readonly now?: Date;
  readonly timeZone?: string;
}

const KEPT_UNAVAILABLE = "EIGHTY_SIXED";
const OFFERED = "OFFERED";
const MALFORMED = "MALFORMED";

/** Whether the item is drawn, and orderable; otherwise the status that hid it. */
export function offering(item: Record<string, unknown>): { readonly available: boolean } | { readonly omitted: string } {
  if (!("availability" in item) || item["availability"] === undefined) {
    // Only an explicit true is orderable: a missing flag is not a promise.
    return { available: item["available"] === true };
  }
  const status = record(item["availability"])?.["status"];
  if (typeof status !== "string") {
    return { omitted: MALFORMED };
  }
  if (status === OFFERED || status === KEPT_UNAVAILABLE) {
    return { available: status === OFFERED };
  }
  return { omitted: status };
}

function wireItems(body: unknown): unknown[] {
  const items = record(record(body)?.["data"])?.["items"];
  return Array.isArray(items) ? items : [];
}

/**
 * What the menu leaves off for its availability, counted by
 * status and sorted by it; "MALFORMED" for an unreadable `availability`. Counts
 * only, so nothing about the item or the diner leaves this function.
 */
export function omittedFromPublicMenu(body: unknown): { status: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const raw of wireItems(body)) {
    const item = record(raw);
    const offered = item === null ? null : offering(item);
    if (offered !== null && "omitted" in offered) {
      counts.set(offered.omitted, (counts.get(offered.omitted) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([status, count]) => ({ status, count }));
}

/** The diner's language from the browser, for `localisedName`. */
export function dinerLanguage(): string {
  return typeof navigator === "undefined" ? "en" : (navigator.languages?.[0] ?? navigator.language ?? "en");
}
